import React, { useState, useEffect } from 'react';
import { string, object, ref } from 'yup';
import { registerLocale } from 'i18n-iso-countries';
import swedishi18n from 'i18n-iso-countries/langs/sv.json';
import Personnummer from 'personnummer';

// Components
import { Layout } from '../../components/Layout';
import { Heading } from '../../components/Heading';
import { Button } from '../../components/Button';
import { InputRadio } from '../../components/InputRadio';
import { InputText } from '../../components/InputText';
import { EditInput } from '../../components/EditInput';
import { ErrorMessage } from '../../components/ErrorMessage';
import InfoIcon from '../../components/InfoIcon';
import InputDropdown from '../../components/InputDropdown';

// Interfaces
import { PersonContactInfo } from '../../interfaces/Person';
import { PaymentInformation } from '../../interfaces/Payment';

// i18n
import { useTranslation } from '../../translations/useTranslation';

// Utils
import { transformYupErrors } from '../../utilities/validation';
import { validateAccountNumber } from '../../utilities/kontonummer';
import { capitalize } from '../../utilities/inputs';
import {
  getAssociation,
  getAutoGiroInfo,
  getBankNumber,
} from '../../utilities/utils';

// Context
import { useForm } from '../../context/form-context/hooks';
import {
  setContactInformation,
  setFormScene,
  setPaymentInformation,
} from '../../context/form-context/actions';

export const UNIONS = [
  {
    label: 'Sveriges Ingenjörer',
    value: '1D874C9F-F3B8-E911-80E0-001DD8B71FB0',
  },
  // {
  //   label: 'Lärarnas Riksförbund (LR)',
  //   value: '319E4D34-F3B8-E911-80E0-001DD8B71FB0',
  // },
  {
    label: 'DIK',
    value: 'B84E7763-F3B8-E911-80E0-001DD8B71FB0',
  },
  {
    label: 'Akavia',
    value: 'EB0CBB5E-5C96-EC11-80FB-001DD8B71FAF',
  },
  {
    label: 'Akademikerförbundet SSR',
    value: '6613A5BC-F3B8-E911-80E0-001DD8B71FB0',
  },
];

registerLocale(swedishi18n);

export const DetailsView: React.FC = () => {
  // Global form state
  const { state, dispatch } = useForm();

  // Hook for translations
  const { t, m, keys, locale } = useTranslation();

  // Hooks for personal and payment information
  const [personalAnswers, setPersonalAnswers] = useState<PersonContactInfo>(
    state.contactInfo
  );
  const [paymentOfChoice, setPaymentOfChoice] = useState<
    'autogiro' | 'invoice' | 'association' | ''
  >(state.paymentInformation?.choice || '');
  const [bankNumber, setBankNumber] = useState<string>(
    getBankNumber(state.paymentInformation)
  );
  const [autoGiroInfo, setautoGiroInfo] = useState<PaymentInformation>(
    getAutoGiroInfo(state.paymentInformation)
  );
  const [association, setAssociation] = useState<string>(
    getAssociation(state.paymentInformation)
  );

  // Hooks for errors
  const [errors, setErrors] = useState<{ [key: string]: string }>();
  const [paymentErrors, setPaymentErrors] = useState<{
    [key: string]: string;
  }>();

  // Hook for determining if everything is correct (everything has been filled in)
  const [isValid, setIsValid] = useState<{
    [key in 'personalAnswers' | 'paymentInformation']: boolean;
  }>({
    personalAnswers: false,
    paymentInformation: false,
  });

  const [bankNumberInputDirty, setBankNumberInputDirty] = useState<boolean>(
    bankNumber ? true : false
  );

  const handleOnBack = () => {
    if (dispatch) {
      dispatch(setFormScene('FORM'));
    }
  };

  useEffect(() => {
    const checkAccountNumber = () => {
      if (!bankNumber) {
        setautoGiroInfo({
          bank: '',
          bankNumber: '',
          clearingNumber: '',
        });
        return;
      }

      const result = validateAccountNumber(bankNumber);

      if (result) {
        setautoGiroInfo({
          bank: result.bankName,
          bankNumber: result.accountNumber,
          clearingNumber: result.sortingCode,
        });
      } else {
        setautoGiroInfo({
          bank: '',
          bankNumber: '',
          clearingNumber: '',
        });
      }
    };

    checkAccountNumber();
  }, [bankNumber]);

  const handleOnChange = (name: string, value: string) => {
    // Update our state
    setPersonalAnswers((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });

    // if we have errors then delete the specific relatated error
    if (errors && errors[name]) {
      setErrors((prevState) => {
        if (prevState) {
          delete prevState[name];
        }
        return prevState;
      });
    }
  };

  const handleOnPaymentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // We now that the value is one of these we cast the value to be one of them
    const value = event.target.value as 'autogiro' | 'invoice' | 'association';
    setPaymentOfChoice(value);

    // Reset any set association if it was set
    if (value !== 'association' && association !== '') {
      setAssociation('');
    }

    if (paymentErrors) {
      setPaymentErrors(undefined);
    }
  };

  const handleOnBankNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Only accepts numbers
    const valueWithoutSpace = event.target.value.replace(/[^\d]/g, '');
    setBankNumber(valueWithoutSpace);
    setBankNumberInputDirty(false);

    if (paymentErrors) {
      setPaymentErrors(undefined);
    }
  };

  const handleOnAssociationChange = (value: string | undefined) => {
    if (value) {
      setAssociation(value);

      // If a user is changing union then set payment of choice to association too
      if (paymentOfChoice !== 'association') {
        setPaymentOfChoice('association');
      }
    }
  };

  const handleOnSubmit = () => {
    Promise.all([validatePersonalAnswers(), validatePaymentInformation()])
      .then((values) => {
        const isValuesValid = values.filter((val) => val === true).length == 2;

        const paymentObject = {
          choice: paymentOfChoice || 'invoice', // fallback to invoice if not specified
          bankInfo: autoGiroInfo,
          viaAssociation: association,
        };

        // Capitalize every field among the personal answers before sending them
        const transformPersonalAnswers = Object.entries(
          personalAnswers
        ).reduce<PersonContactInfo>(
          (accum, [key, value]) => ({
            ...accum,
            [key]: key === 'email' || key === 'emailRepeated' ? value : capitalize(value), // Only capitalize a field if it's not the email field
          }),
          {} as PersonContactInfo
        );

        if (isValuesValid) {
          if (dispatch && state.contactInfo?.personalIdentityNumber) {
            dispatch(
              setContactInformation({
                ...transformPersonalAnswers,
                // Disable edit of personal identity number if form mode eId
                ...(state.formMode === 'eId' && {
                  personalIdentityNumber:
                    state.contactInfo.personalIdentityNumber,
                }),
              })
            );
            dispatch(setPaymentInformation(paymentObject));
            dispatch(setFormScene('CHECKOUT'));
          }
        }
      })
      .catch(() => {});
  };

  const validatePersonalAnswers = () => {
    const requiredErrorMessage = t(keys.errorRequired);
    const emailMalformedErrorMessage = t(keys.errorInvalidEmail);
    const postalCodeMinLengthErrorMessage = m(
      keys.errorTooShort,
      '{length}',
      '5'
    );
    const postalCodeMaxLengthErrorMessage = m(
      keys.errorTooLong,
      '{length}',
      '6'
    );
    const phoneNumberLengthErrorMessage = m(
      keys.errorTooLong,
      '{length}',
      '20'
    );
    const max50LengthErrorMessage = m(keys.errorTooLong, '{length}', '50');

    const schema = object().shape({
      // Only validate personal identity number if form mode is no-eId
      ...(state.formMode === 'no-eId' && {
        personalIdentityNumber: string()
          .required(requiredErrorMessage)
          .test({
            name: 'is-valid-personal-identity-number',
            test: (value) => {
              if (value) {
                return Personnummer.valid(value);
              }
              return false;
            },
            message: t(keys.errorInvalidPersonalID),
          }),
      }),
      firstName: string().required(requiredErrorMessage),
      familyName: string().required(requiredErrorMessage),
      street: string()
        .max(50, max50LengthErrorMessage)
        .required(requiredErrorMessage),
      postalCode: string()
        .trim()
        .min(5, postalCodeMinLengthErrorMessage)
        .max(6, postalCodeMaxLengthErrorMessage)
        .required(requiredErrorMessage),
      city: string()
        .trim()
        .max(50, max50LengthErrorMessage)
        .required(requiredErrorMessage),
      email: string()
        .email(emailMalformedErrorMessage)
        .required(requiredErrorMessage),
        emailRepeated: string()
        .required(t(keys.errorRequired))
        .oneOf(
          [ref('email')],
          m(
            keys.errorMismatch,
            '{otherField}',
            t(keys.formEmailAddress).toLocaleLowerCase()
          )
        ),
      phoneNumber: string()
        .max(20, phoneNumberLengthErrorMessage)
        .required(requiredErrorMessage),
    });

    const isValid = schema
      .validate(personalAnswers, { abortEarly: false })
      .then(() => {
        setIsValid((prevState) => ({
          ...prevState,
          personalAnswers: true,
        }));
        return true;
      })
      .catch((error) => {
        if (error.name === 'ValidationError') {
          const errors = transformYupErrors(error.inner);
          setErrors(errors);
          setIsValid((prevState) => ({
            ...prevState,
            personalAnswers: false,
          }));
        }
        return false;
      });

    return isValid;
  };

  const validatePaymentInformation = () => {
    const requiredErrorMessage = t(keys.errorRequired);
    const oneOfPaymentsErrorMessage = t(keys.errorOneOfPayments);
    const viaAssociationErrorMessage = t(keys.errorChooseAssociation);

    const schema = object({
      choice: string()
        .oneOf(
          ['invoice', 'autogiro', 'association'],
          oneOfPaymentsErrorMessage
        )
        .required(requiredErrorMessage),
      bankInfo: object().when('choice', {
        is: 'autogiro',
        then: object({
          bank: string(),
          clearingNumber: string(),
          bankNumber: string().required(requiredErrorMessage),
        }),
        otherwise: object().notRequired(),
      }),
      viaAssociation: string().when('choice', {
        is: 'association',
        then: string().required(viaAssociationErrorMessage),
        otherwise: string().notRequired(),
      }),
    });

    const paymentObject = {
      choice: paymentOfChoice,
      bankInfo: autoGiroInfo,
      viaAssociation: association,
    };

    const isValid = schema
      .validate(paymentObject, { abortEarly: false })
      .then(() => {
        setIsValid((prevState) => ({
          ...prevState,
          paymentInformation: true,
        }));
        return true;
      })
      .catch((error) => {
        if (error.name === 'ValidationError') {
          const errors = transformYupErrors(error.inner);
          setPaymentErrors(errors);
          setIsValid((prevState) => ({
            ...prevState,
            paymentInformation: false,
          }));
        }
        return false;
      });

    return isValid;
  };

  const personalIdentityNumber = errors?.personalIdentityNumber;
  const firstNameErrorMsg = errors?.firstName;
  const familyNameErrorMsg = errors?.familyName;
  const streetErrorMsg = errors?.street;
  const postalCodeErrorMsg = errors?.postalCode;
  const cityErrorMsg = errors?.city;
  const countryErrorMsg = errors?.country;
  const emailErrorMsg = errors?.email;
  const emailRepeatedErrorMsg = errors?.emailRepeated;
  const phoneNumberErrorMsg = errors?.phoneNumber;

  const paymentChoiceErrorMsg = paymentErrors?.choice;

  const anyBankError =
    paymentErrors &&
    (paymentErrors['bankInfo.bank'] ||
      paymentErrors['bankInfo.clearingNumber'] ||
      paymentErrors['bankInfo.bankNumber']);

  const hasAnyErrors =
    (errors && Object.values(errors).length > 0) ||
    (paymentErrors && Object.values(paymentErrors).length > 0);

  return (
    <Layout viewId={'details'}>
      <h1 className="text-2xl margin-bottom-sm">
        {state.formMode === 'eId'
          ? t(keys.headingCorrectInformation)
          : t(keys.headingFillContactDetails)}
      </h1>

      <div className="flex flex-column gap-xs">
        {state.formMode === 'no-eId' && (
          <EditInput
            label={t(keys.formPersonalIdentityNumber) + ' *'}
            name={'personalIdentityNumber'}
            defaultValue={personalAnswers.personalIdentityNumber}
            errorMessage={personalIdentityNumber}
            onChange={handleOnChange}
          />
        )}

        <EditInput
          label={t(keys.formFirstName) + ' *'}
          name={'firstName'}
          autoComplete={'given-name'}
          defaultValue={personalAnswers.firstName}
          errorMessage={firstNameErrorMsg}
          onChange={handleOnChange}
        />

        <EditInput
          label={t(keys.formFamilyName) + ' *'}
          name={'familyName'}
          autoComplete={'family-name'}
          defaultValue={personalAnswers.familyName}
          errorMessage={familyNameErrorMsg}
          onChange={handleOnChange}
        />

        <EditInput
          label={t(keys.formStreetName) + ' *'}
          name={'street'}
          autoComplete={'street-address'}
          defaultValue={personalAnswers.street}
          errorMessage={streetErrorMsg}
          onChange={handleOnChange}
        />

        <EditInput
          type={'number'}
          label={t(keys.formPostalCode) + ' *'}
          name={'postalCode'}
          autoComplete={'postal-code'}
          defaultValue={personalAnswers.postalCode.replace(/\s/g, '')}
          errorMessage={postalCodeErrorMsg}
          onChange={handleOnChange}
          onlyNumbers
        />

        <EditInput
          label={t(keys.formCity) + ' *'}
          name={'city'}
          defaultValue={personalAnswers.city}
          errorMessage={cityErrorMsg}
          onChange={handleOnChange}
        />

        <EditInput
          label={t(keys.formCountry)}
          name={'country'}
          autoComplete={'country-name'}
          defaultValue={state.contactInfo.country}
          errorMessage={countryErrorMsg}
          onChange={handleOnChange}
        />

        <EditInput
          label={t(keys.formEmailAddress) + ' *'}
          name={'email'}
          type={'email'}
          autoComplete={'email'}
          defaultValue={personalAnswers.email}
          disablePaste={true}
          errorMessage={emailErrorMsg}
          onChange={handleOnChange}
        />

      <EditInput
          label={t(keys.formEmailAddressRepeated) + ' *'}
          name={'emailRepeated'}
          type={'email'}
          autoComplete={'email'}
          defaultValue={personalAnswers.emailRepeated}
          disablePaste={true}
          errorMessage={emailRepeatedErrorMsg}
          onChange={handleOnChange}
        />

        <EditInput
          label={t(keys.formPhoneNumber) + ' *'}
          name={'phoneNumber'}
          type={'tel'}
          defaultValue={personalAnswers.phoneNumber}
          autoComplete={'tel'}
          errorMessage={phoneNumberErrorMsg}
          onChange={handleOnChange}
        />
      </div>

      <div className="flex flex-column gap-xs padding-bottom-sm border-bottom border-contrast-low margin-bottom-sm">
        <h2 className="text-2xl margin-top-sm">{t(keys.headingHowToPay)}</h2>
        <div className="flex flex-column gap-2xs">
          {state.formMode === 'eId' && (
            <div>
              <InputRadio
                label={t(keys.paymentAutogiro)}
                value={'autogiro'}
                name={'paymentOfChoice'}
                onChange={handleOnPaymentChange}
                checked={paymentOfChoice === 'autogiro'}
              />
              {paymentOfChoice === 'autogiro' && (
                <div
                  className={
                    'margin-top-2xs border-left border-contrast-low padding-left-sm'
                  }
                >
                  <InputText
                    name={'bankNumber'}
                    label={t(keys.paymentBankNumberLabel)}
                    value={bankNumber}
                    errorMessage={
                      anyBankError ? t(keys.errorInvalidBankAccount) : undefined
                    }
                    onChange={handleOnBankNumberChange}
                    onBlur={() => setBankNumberInputDirty(true)}
                    autoComplete={'off'}
                    tooltipContent={
                      locale === 'en' ? (
                        <div className="text-component">
                          <p className="text-sm font-semibold">
                            You must specify an account that you are the owner
                            of.
                          </p>
                          <ul className="text-sm">
                            <li>
                              The clearing number is usually four digits. It is
                              usually stated on the bank statement.
                            </li>
                            <li>
                              Swedbank's clearing number can have five digits,
                              for example, 8237-9 XXX.
                            </li>
                            <li>
                              Handelsbanken's clearing number always starts with
                              6. If you can't find it, you can enter 6000.
                            </li>
                            <li>
                              Nordea's (personal account with personal
                              identification number) is 3300.
                            </li>
                          </ul>
                        </div>
                      ) : (
                        <div className="text-component text-component--tight">
                          <p className="text-sm font-semibold">
                            Du behöver stå som innehavare av kontot.
                          </p>
                          <ul className="text-sm color-white">
                            <li>
                              Clearingnumret är oftast fyrsiffrigt. Du hittar
                              det i din bank.
                            </li>
                            <li>
                              Swedbanks clearingnummer kan ha 5 siffror, t.e.x.
                              8237-9.
                            </li>
                            <li>
                              Handelsbanksens clearingnummer börjar alltid med
                              6. Ange 6000 om du inte hittar det i din bank.
                            </li>
                            <li>
                              Nordeas personkontos clearingnummer är 3300 om du
                              har ditt personnummer som kontonummer.
                            </li>
                          </ul>
                        </div>
                      )
                    }
                  />
                  {!bankNumber && (
                    <p className="bg-info padding-y-3xs text-sm padding-x-2xs margin-top-2xs radius-sm">
                      {t(keys.paymentBankHelpText)}
                    </p>
                  )}
                  {autoGiroInfo.bank && (
                    <p className="bg-light-green padding-y-3xs text-sm padding-x-2xs margin-top-2xs radius-sm">
                      Bank:{' '}
                      <span className="font-medium">{autoGiroInfo.bank}</span>
                    </p>
                  )}
                  {!autoGiroInfo.bank && bankNumber && bankNumberInputDirty && (
                    <p className="bg-warning padding-y-3xs text-sm padding-x-2xs margin-top-2xs radius-sm">
                      {t(keys.warningInvalidBankAccount)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <InputRadio
              label={t(keys.paymentInvoice)}
              value={'invoice'}
              name={'paymentOfChoice'}
              onChange={handleOnPaymentChange}
              checked={paymentOfChoice === 'invoice'}
            />
            {paymentOfChoice === 'invoice' && (
              <div
                className={
                  'margin-top-2xs border-left border-contrast-low padding-left-sm'
                }
              >
                <div className="text-component text-component--tight bg-info padding-y-xs padding-x-2xs radius-sm">
                  <p className="font-medium">{t(keys.paymentInvoiceKivra)}</p>
                  <a
                    href="https://www.akademikernasakassa.se/kivra"
                    target="_blank"
                  >
                    {t(keys.paymentInvoiceKivraLink)}
                  </a>
                </div>
              </div>
            )}
          </div>
          <div>
            <InputRadio
              label={t(keys.paymentViaAssociation)}
              value={'association'}
              name={'paymentOfChoice'}
              onChange={handleOnPaymentChange}
              checked={paymentOfChoice === 'association'}
            />
            {paymentOfChoice === 'association' && (
              <div
                className={
                  'margin-top-2xs border-left border-contrast-low padding-left-sm'
                }
              >
                <InputDropdown
                  wrapperClassName="membership-app__dropdown"
                  placeholder={t(keys.paymentChooseAssociation)}
                  value={association}
                  label={t(keys.paymentViaAssociationDescription)}
                  choices={UNIONS}
                  errorMessage={paymentErrors?.viaAssociation}
                  onChange={handleOnAssociationChange}
                />
              </div>
            )}
            {paymentChoiceErrorMsg && (
              <p className='bg-error-high border border-error padding-y-3xs text-sm padding-x-2xs margin-top-2xs radius-sm'>
                {paymentChoiceErrorMsg}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-column gap-2xs">
          <div className="bg-light-green padding-xs radius-md">
            <div className="flex flex-column gap-2xs">
              <h2 className="text-base font-primary font-semibold letter-spacing-reset flex gap-3xs items-center">
                {' '}
                <InfoIcon size="md" /> 
                <span>{t(keys.paymentEInvoiceTitle)}</span>
              </h2>
              <p>{t(keys.paymentEInvoiceDescription)}</p>
              <a
                className="link"
                href="https://www.akademikernasakassa.se/efaktura"
                target="_blank"
              >
                {t(keys.paymentEInvoiceLink)}
              </a>
            </div>
          </div>
          {state.formMode === 'no-eId' && (
            <div className="bg-light-blue padding-xs radius-md">
              <div className="flex flex-column gap-2xs">
                <h2 className="text-base font-primary font-semibold letter-spacing-reset flex gap-3xs items-center">
                  <InfoIcon size="md" />
                  <span>{t(keys.paymentDirectDebitTitle)}</span>
                </h2>
                <p>{t(keys.paymentDirectDebitDescription)}</p>
                <a
                 className="link"
                  href="https://www.akademikernasakassa.se/autogiro"
                  target="_blank"
                >
                  {t(keys.paymentDirectDebitLink)}
                </a>
              </div>
            </div>
          )}
        </div>

        {state.formMode === 'no-eId' && state.enableMemberCodeField && (
          <div>
            <h2 className="text-2xl margin-bottom-xs margin-top-2xs">
              {t(keys.headingMemberCode)}
            </h2>
            <p className="margin-bottom-2xs">{t(keys.memberCodeDescription)}</p>
            <EditInput
              label={t(keys.formMemberCode)}
              name="memberCode"
              type="text"
              defaultValue={personalAnswers.memberCode}
              onChange={handleOnChange}
            />
          </div>
        )}
      </div>

      {/* {hasAnyErrors && (
          <ErrorMessage message={t(keys.errorFillInRequiredFields)} />
        )} */}
      <div className="flex flex-wrap gap-2xs margin-bottom-xs">
        <Button
          id="membership-app-details-next"
          disabled={hasAnyErrors}
          onClick={handleOnSubmit}
          title={t(keys.uiButtonNext)}
        />
        <Button
          layout="secondary"
          onClick={handleOnBack}
          title={t(keys.uiButtonGoBack)}
        />
      </div>
      <p className="text-component">
        {t(keys.submissionPolicy)}{' '}
        <a
          href="https://www.akademikernasakassa.se/gdpr"
          title={t(keys.submissionPolicyLinkTitle)}
          target="_blank"
        >
          {t(keys.submissionPolicyLinkTitle)}
        </a>
        .
      </p>
    </Layout>
  );
};
